import type { CaseId } from "@/queries/useFetchCaseIdsForActiveFilter.ts";
import { Box, Button, Chip, Typography } from "@mui/material";
import { X } from "lucide-react";

type Props = {
    caseIds: CaseId[];
    selectedCases: CaseId[];
    setSelectedCases: (caseIds: CaseId[]) => void;
};

const ActiveCaseFilterIds = ({ caseIds, selectedCases, setSelectedCases }: Props) => {
    const isCaseSelected = (caseId: string) =>
        selectedCases.some((selected) => selected.id === caseId);

    const handleCaseToggle = (caseId: string) => {
        const existing = selectedCases.find((c) => c.id === caseId);

        if (existing) {
            setSelectedCases(selectedCases.filter((c) => c.id !== caseId));
        } else {
            const toAdd = caseIds.find((c) => c.id === caseId);

            if (toAdd) {
                setSelectedCases([...selectedCases, toAdd]);
            }
        }
    };

    const handleSelectAll = () => {
        setSelectedCases(caseIds);
    };

    const handleDeselectAll = () => {
        setSelectedCases([]);
    };

    const handleRemoveCase = (caseId: string) => {
        setSelectedCases(selectedCases.filter((c) => c.id !== caseId));
    };

    return (
        <Box sx={{ marginTop: (theme) => theme.spacing(2) }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: (theme) => theme.spacing(2),
                    padding: (theme) => theme.spacing(1),
                    backgroundColor: (theme) => theme.palette.background.default,
                    borderRadius: (theme) => theme.shape.borderRadius,
                }}
            >
                <Box>
                    <Button
                        size="small"
                        onClick={handleSelectAll}
                        disabled={selectedCases.length === caseIds.length}
                    >
                        Select All
                    </Button>
                    <Button
                        size="small"
                        onClick={handleDeselectAll}
                        disabled={selectedCases.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Deselect All
                    </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Click chips to toggle selection
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: (theme) => theme.spacing(1),
                    maxHeight: 300,
                    overflowY: "auto",
                    padding: (theme) => theme.spacing(1),
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: (theme) => theme.shape.borderRadius,
                    backgroundColor: (theme) => theme.palette.background.default,
                }}
            >
                {caseIds.map((caseItem) => {
                    const isSelected = isCaseSelected(caseItem.id);
                    return (
                        <Chip
                            key={caseItem.id}
                            label={`${caseItem.id} - ${caseItem.clientName}`}
                            onClick={() => handleCaseToggle(caseItem.id)}
                            onDelete={isSelected ? () => handleRemoveCase(caseItem.id) : undefined}
                            deleteIcon={isSelected ? <X size={16} /> : undefined}
                            clickable
                            variant={isSelected ? "filled" : "outlined"}
                            sx={{
                                backgroundColor: (theme) =>
                                    isSelected
                                        ? theme.palette.primary.light
                                        : theme.palette.background.paper,
                                border: (theme) =>
                                    `1px solid ${
                                        isSelected
                                            ? theme.palette.primary.main
                                            : theme.palette.divider
                                    }`,
                                "& .MuiChip-label": {
                                    fontWeight: isSelected ? 500 : 400,
                                },
                                "& .MuiChip-deleteIcon": {
                                    color: (theme) =>
                                        isSelected
                                            ? theme.palette.primary.main
                                            : theme.palette.action.active,
                                },
                            }}
                        />
                    );
                })}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Selected cases will be updated with the chosen assignments. Click chips to toggle
                selection or use the X to remove.
            </Typography>
        </Box>
    );
};

export default ActiveCaseFilterIds;
